// `select` — choose from a list of values (docs/plans/transaction-wizard.md
// §6.2). The source is the column's OWN lookup / edit-mask values (preferred:
// one definition of what may go in that column), an ad-hoc lookup, fixed
// values, existing records of the base transaction, or the line form's Line
// Entry source. Every lookup goes through the screen runtime's own lookup
// transport, so its filters and rights are the form's.

import { useEffect, useMemo, useRef, useState } from 'react'
import { Check as CheckIcon, Loader2, Search } from 'lucide-react'
import { lineEntryApi } from '../../txn/lineEntry/contract'
import type { ListFilter } from '../../txn/screen/contract'
import { buildParentPk, columnDbName, columnFormKey, hasLookup } from '../../txn/utils'
import { columnOf, headingOf, lookupFilter, parseTarget, readCol, renderMap, text, type Row } from '../model'
import { T, button, input as inputStyle, muted } from '../styles'
import { tw } from '../../testHooks'
import type { StepProps } from './common'
import { display } from './common'

interface Option { data: unknown; display: string; row: Row }

const PAGE = 50

export default function SelectStep({ ctl, handheld }: StepProps) {
  const { step, scope, fi, frame, base, screen, host, ui } = ctl
  const [search, setSearch] = useState('')
  const [options, setOptions] = useState<Option[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [picked, setPicked] = useState<number[]>([])
  const autoPicked = useRef('')
  const form = String(step?.form_no || ctl.model?.line_form || fi?.lineForm || '')
  const target = step?.target && fi ? parseTarget(step.target, fi, form) : null
  const src = step?.source || {}
  const serverSearch = !src.values
  const searchable = step?.search !== false

  // What the lookup's filters may read ({form.X} / {header.X}).
  const formValues = scope?.line || scope?.header || {}
  const headerValues = scope?.header || {}
  const sig = JSON.stringify([step?.id, ui.key, search, formValues, headerValues])

  useEffect(() => {
    if (!step || !fi || !frame || !base || !screen || !scope) return
    let live = true
    setError(null)
    const run = async (): Promise<Option[]> => {
      if (src.values) {
        return src.values.map(v => ({ data: v.data, display: String(v.display ?? v.data ?? ''), row: { data: v.data, display: v.display } }))
      }
      if (src.records) {
        const { values } = renderMap(src.records.filter || {}, scope)
        const filters: ListFilter[] = Object.entries(values).map(([col, value]) => {
          const c = columnOf(fi, fi.headerNo, col)
          return { column: c ? columnDbName(c) : col.toLowerCase(), op: 'eq', value }
        })
        if (search) {
          const first = (src.records.columns || step.columns || [])[0]
          if (first) filters.push({ column: first.toLowerCase(), op: 'contains', value: search })
        }
        const res = await screen.list(base.obj_name, { limit: PAGE, offset: 0, explicitLimit: true, filters, bypass: true })
        const cols = src.records.columns || step.columns || []
        return (res.rows || []).map(r => ({
          data: r._txn_id,
          display: cols.length ? cols.map(c => display(readCol(r, fi, fi.headerNo, c))).filter(Boolean).join(' · ') : String(r._txn_id),
          row: r,
        }))
      }
      if (src.line_source) {
        const sm = fi.forms[fi.headerNo]?.sm
        const api = lineEntryApi((a, p) => screen.execute(base.obj_name, a, p), {
          formNo: form, headerValues: frame.record.header, txnId: frame.txn_id || undefined,
          parentPk: sm ? buildParentPk(sm, frame.record.header) : undefined,
          lines: frame.record.details[form] || [],
        })
        const res = await api.fetch(src.line_source, { search, limit: PAGE })
        return (res.rows || []).map(r => {
          const vals = r.values || {}
          const shown = (step.columns?.length ? step.columns : Object.keys(r._display || vals).slice(0, 3))
            .map(c => display((r._display || {})[c] ?? vals[c])).filter(Boolean)
          return { data: r._src_key, display: shown.join(' · ') || r._src_key, row: { ...vals, _src_key: r._src_key, data: r._src_key } }
        })
      }
      // The column's own values / lookup, or an ad-hoc lookup.
      const colName = src.column || target?.name || ''
      const colForm = target && target.scope !== 'var' ? target.formNo : form
      const col = colName ? columnOf(fi, colForm, colName) : null
      if (!src.lookup && col?.edit_mask?.values?.length) {
        return col.edit_mask.values.map(v => ({ data: v.data, display: String(v.display ?? v.data), row: { data: v.data, display: v.display } }))
      }
      const lookup = src.lookup || (col && hasLookup(col) ? col.lookup : '')
      if (!lookup && !col?.lookup_sql) throw new Error('This list has no values to choose from.')
      const res = await screen.txn.lookup(host.project, base.obj_name, String(lookup || ''), search, PAGE, src.lookup ? {
        ...lookupFilter(src.filter, scope), formValues, headerValues,
      } : {
        filter: col?.lookup_filter, formValues, headerValues,
        lookupSql: col?.lookup_sql, lookupSqlDataCol: col?.lookup_sql_data_col,
        lookupField: col ? columnFormKey(col) : undefined,
      })
      return (res.rows || []).map(r => ({ data: r.data, display: String(r.display ?? r.data ?? ''), row: r as unknown as Row }))
    }
    const timer = setTimeout(() => {
      run().then(o => { if (live) setOptions(o) })
        .catch(err => { if (live) { setOptions([]); setError(err instanceof Error ? err.message : String(err)) } })
    }, search ? 250 : 0)
    return () => { live = false; clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig])

  const shown = useMemo(() => {
    if (!options) return []
    if (serverSearch || !search) return options
    const q = search.toLowerCase()
    return options.filter(o => o.display.toLowerCase().includes(q) || String(o.data ?? '').toLowerCase().includes(q))
  }, [options, search, serverSearch])

  // One choice and nothing typed: pick it (auto_pick_single).
  useEffect(() => {
    if (!step?.auto_pick_single || step.multi || search || !options || options.length !== 1) return
    if (autoPicked.current === ui.key) return
    autoPicked.current = ui.key
    void ctl.select([{ ...options[0].row, data: options[0].data }])
  }, [options, step, search, ui.key, ctl])

  if (!step || !fi) return null
  const current = target
    ? (target.scope === 'var' ? frame?.vars[target.name] : readCol(target.scope === 'header' ? frame?.record.header : scope?.line, fi, target.formNo, target.name))
    : undefined
  const choose = (o: Option) => {
    if (step.multi) {
      const i = shown.indexOf(o)
      setPicked(p => (p.includes(i) ? p.filter(x => x !== i) : [...p, i]))
      return
    }
    void ctl.select([{ ...o.row, data: o.data }])
  }
  const isCurrent = (o: Option) => !step.multi && current != null && String(current) === String(o.data)
  const mode = step.display || (shown.length <= 8 && handheld ? 'tiles' : 'list')
  const label = target && target.scope !== 'var' ? headingOf(fi, target.formNo, target.name) : (step.title || 'Choose')

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} {...tw('field', step.target || step.id)}>
      {searchable && mode !== 'dropdown' && (
        <div style={{ position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: T.muted }} />
          <input style={{ ...inputStyle(handheld), paddingLeft: 32 }} placeholder="Search…" value={search}
            onChange={e => setSearch(e.target.value)} {...tw('field', '_wizard_search')} aria-label={`Search ${label}`} />
        </div>
      )}
      {options === null && <div style={{ ...muted, display: 'flex', gap: 8, alignItems: 'center' }}><Loader2 size={14} /> Loading…</div>}
      {error && <div style={{ color: '#a61b1b', fontSize: 14 }}>{error}</div>}
      {options !== null && !shown.length && !error && <div style={muted}>Nothing to choose from.</div>}
      {mode === 'dropdown' ? (
        <select style={inputStyle(handheld)} aria-label={label}
          value={current == null ? '' : String(current)}
          onChange={e => { const o = shown.find(x => String(x.data) === e.target.value); if (o) choose(o) }}>
          <option value="">— choose —</option>
          {shown.map((o, i) => <option key={i} value={String(o.data)}>{o.display}</option>)}
        </select>
      ) : mode === 'tiles' ? (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${handheld ? 140 : 160}px, 1fr))`, gap: 8 }}>
          {shown.map((o, i) => (
            <button key={i} type="button" onClick={() => choose(o)} {...tw('row', i)}
              style={{
                ...button(handheld, isCurrent(o) || picked.includes(i)), minHeight: handheld ? 64 : 48,
                whiteSpace: 'normal', textAlign: 'center', justifyContent: 'center',
              }}>
              {o.display}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden', maxHeight: handheld ? undefined : 420, overflowY: 'auto' }}>
          {shown.map((o, i) => {
            const on = isCurrent(o) || picked.includes(i)
            return (
              <button key={i} type="button" onClick={() => choose(o)} {...tw('row', i)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                  minHeight: handheld ? 52 : 38, padding: '6px 12px', border: 'none',
                  borderBottom: `1px solid ${T.border}`, background: on ? 'var(--color-accent-soft, #e8f0fe)' : T.bg,
                  color: T.text, fontFamily: 'inherit', fontSize: handheld ? 16 : 14, cursor: 'pointer',
                }}>
                {step.multi && <input type="checkbox" readOnly checked={on} style={{ width: 18, height: 18 }} />}
                <span style={{ flex: 1 }}>
                  {o.display}
                  {(step.columns || []).length > 0 && !src.records && (
                    <span style={{ ...muted, marginLeft: 8 }}>
                      {(step.columns || []).map(c => display(o.row[c] ?? o.row[c.toLowerCase()])).filter(Boolean).join(' · ')}
                    </span>
                  )}
                </span>
                {on && !step.multi && <CheckIcon size={16} />}
              </button>
            )
          })}
        </div>
      )}
      {step.multi && (
        <div>
          <button type="button" style={button(handheld, true, !picked.length)} disabled={!picked.length}
            onClick={() => void ctl.select(picked.map(i => ({ ...shown[i].row, data: shown[i].data })))}
            {...tw('action', 'add_selected')}>
            Add {picked.length || ''} {picked.length === 1 ? 'line' : 'lines'}
          </button>
        </div>
      )}
    </div>
  )
}
